// WalletTabs.tsx
import type React from "react";
import { motion } from "framer-motion";
import { WalletTransaction, WalletTransactionType, WalletTransactionStatus } from "../../../types/stores";
import { TFunction } from "i18next";
import StatsIcons from "./StatsIcons";
import { StatCard } from "./StatCard";

// ================== Icons ==================


// ================== Types ==================



interface AnalyticsTabProps {
  t: TFunction;
  stats: any;
  totalTransactions: number;
  recentTransactions: WalletTransaction[];
  localFilteredTransactions: WalletTransaction[];
  displayBalance: number;
  withdrawAmount: string;
  setWithdrawAmount: (val: string) => void;
  handleWithdraw: () => void;
  isWithdrawing: boolean;
  exportReport: () => void;
  getTransactionTypeText: (type: WalletTransactionType) => string;
  getStatusColor: (status: WalletTransactionStatus) => string;
}

// ================== Helper Components ==================





// ================== Overview Tab ==================

// ================== Transactions Tab ==================


// ================== Analytics Tab ==================

export const renderAnalyticsTab = ({
  t,
  stats,
  totalTransactions,
  recentTransactions,
  localFilteredTransactions,
  displayBalance,
  withdrawAmount,
  setWithdrawAmount,
  handleWithdraw,
  isWithdrawing,
  exportReport,
  getTransactionTypeText,
  getStatusColor,
}: AnalyticsTabProps) => {
  return (
    <div className="space-y-8">
      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={<StatsIcons.wallet />}
          title={t('wallet.availableBalance', 'Available Balance')}
          value={`$${displayBalance?.toFixed(2)}`}
          subtitle="Ready for withdrawal"
          color="blue"
        />
        
        <StatCard
          icon={<StatsIcons.revenue />}
          title={t('wallet.totalRevenue', 'Total Revenue')}
          value={`$${stats.totalRevenue.toFixed(2)}`}
          subtitle="All time"
          color="green"
        />
        
        <StatCard
          icon={<StatsIcons.withdraw />}
          title={t('wallet.totalWithdrawals', 'Total Withdrawals')}
          value={`$${stats.totalWithdrawals.toFixed(2)}`}
          subtitle="Processed"
          color="indigo"
        />
        
        <StatCard
          icon={<StatsIcons.clock />}
          title={t('wallet.pendingBalance', 'Pending Balance')}
          value={`$${stats.pendingBalance.toFixed(2)}`}
          subtitle="Awaiting clearance"
          color="amber"
        />
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Withdrawal & Quick Stats */}
        <div className="lg:col-span-1 space-y-6">
          {/* Withdrawal Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
          >
            <div className="flex items-center space-x-2 mb-6">
              <div className="p-2 bg-blue-100 rounded-lg">
                <StatsIcons.withdraw />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('wallet.withdrawFunds', 'Withdraw Funds')}
              </h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('wallet.amount', 'Amount')}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                    $
                  </span>
                  <input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    max={displayBalance}
                  />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-sm text-gray-500">
                    {t('wallet.available', 'Available')}: <span className="font-medium">${displayBalance?.toFixed(2)}</span>
                  </p>
                  <button
                    onClick={() => setWithdrawAmount(displayBalance?.toString() || '0')}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    {t('wallet.useMax', 'Use max')}
                  </button>
                </div>
              </div>

              <button
                onClick={handleWithdraw}
                disabled={isWithdrawing || !withdrawAmount || parseFloat(withdrawAmount) <= 0}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center space-x-2"
              >
                <StatsIcons.withdraw />
                <span>
                  {isWithdrawing ? t('wallet.processing', 'Processing...') : t('wallet.withdrawNow', 'Withdraw Now')}
                </span>
              </button>

              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                <div className="flex items-start space-x-2">
                  <StatsIcons.info />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">{t('wallet.withdrawalInfo', 'Withdrawal Information')}</p>
                    <p className="mt-1">{t('wallet.withdrawalNote', 'Withdrawals are processed within 24-48 hours. Minimum withdrawal amount is $10.')}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Quick Stats */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              {t('wallet.performanceMetrics', 'Performance Metrics')}
            </h3>
            
            <div className="space-y-4">
              {[
                { label: t('wallet.successRate', 'Success Rate'), value: `${stats.successRate.toFixed(1)}%`, color: 'text-emerald-600' },
                { label: t('wallet.avgTransaction', 'Avg. Transaction'), value: `$${stats.averageTransaction.toFixed(2)}`, color: 'text-blue-600' },
                { label: t('wallet.highestTransaction', 'Highest Transaction'), value: `$${stats.highestTransaction.toFixed(2)}`, color: 'text-purple-600' },
                { label: t('wallet.totalTransactions', 'Total Transactions'), value: totalTransactions.toString(), color: 'text-gray-900' },
                { label: t('wallet.successful', 'Successful'), value: stats.successfulTransactions.toString(), color: 'text-green-600' },
                { label: t('wallet.pending', 'Pending'), value: stats.pendingTransactions.toString(), color: 'text-amber-600' },
              ].map((metric, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <span className="text-sm text-gray-600">{metric.label}</span>
                  <span className={`text-sm font-medium ${metric.color}`}>
                    {metric.value}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Right Column - Analytics & Export */}
        <div className="lg:col-span-2 space-y-6">
          {/* Analytics Dashboard */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-white rounded-lg shadow-sm border">
                  <StatsIcons.chart />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('wallet.analyticsDashboard', 'Analytics Dashboard')}
                </h3>
              </div>
              <span className="text-sm text-gray-500">
                Real-time data
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Transaction Status Chart */}
              <div className="bg-white p-5 rounded-xl border border-gray-200">
                <h4 className="font-medium text-gray-700 mb-4">
                  {t('wallet.transactionStatus', 'Transaction Status')}
                </h4>
                <div className="flex items-center justify-center h-40">
                  <div className="relative w-32 h-32">
                    {[
                      { status: 'completed', count: stats.successfulTransactions, color: '#10B981' },
                      { status: 'pending', count: stats.pendingTransactions, color: '#F59E0B' },
                      { status: 'failed', count: stats.failedTransactions, color: '#EF4444' },
                    ].map((item, index) => {
                      const total = stats.successfulTransactions + stats.pendingTransactions + stats.failedTransactions;
                      const percentage = total > 0 ? (item.count / total) * 100 : 0;
                      return (
                        <div key={item.status} className="absolute inset-0 flex items-center justify-center">
                          <div
                            className="absolute w-32 h-32 rounded-full border-8 border-gray-100"
                            style={{
                              clipPath: `polygon(50% 50%, 50% 0%, ${50 + 50 * Math.cos((index * 120) * Math.PI / 180)}% ${50 + 50 * Math.sin((index * 120) * Math.PI / 180)}%)`,
                              backgroundColor: item.color + '40'
                            }}
                          />
                          <div className="relative z-10 text-center">
                            <div className="text-lg font-bold" style={{ color: item.color }}>
                              {item.count}
                            </div>
                            <div className="text-xs text-gray-500">
                              {item.status}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              {/* Revenue Chart */}
              <div className="bg-white p-5 rounded-xl border border-gray-200">
                <h4 className="font-medium text-gray-700 mb-4">
                  {t('wallet.revenueOverview', 'Revenue Overview')}
                </h4>
                <div className="space-y-3">
                  {[
                    { period: 'Today', amount: stats.todayRevenue, color: 'bg-green-500', max: 5000 },
                    { period: 'This Week', amount: stats.weeklyRevenue, color: 'bg-blue-500', max: 20000 },
                    { period: 'This Month', amount: stats.monthlyRevenue, color: 'bg-purple-500', max: 80000 },
                  ].map((item, index) => (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-600">{item.period}</span>
                        <span className="text-sm font-medium">${item.amount.toFixed(2)}</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${item.color}`}
                          style={{ width: `${Math.min((item.amount / item.max) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Export Tools */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              {t('wallet.exportTools', 'Export Tools')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <button
                onClick={exportReport}
                className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all duration-200 group"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-white rounded-lg shadow-sm border">
                    <StatsIcons.download />
                  </div>
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900 group-hover:text-blue-700">
                      {t('wallet.fullReport', 'Full Report')}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {t('wallet.exportJSONDesc', 'Export complete data in JSON format')}
                    </p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => {
                  // Generate CSV
                  const headers = ['Date', 'Type', 'Amount', 'Fee', 'Status', 'Reference'];
                  const csvRows = [
                    headers.join(','),
                    ...localFilteredTransactions.map(tx => [
                      new Date(tx.created_at).toLocaleDateString(),
                      getTransactionTypeText(tx.type),
                      tx.amount,
                      tx.fee || '0',
                      tx.status,
                      tx.reference_id || ''
                    ].join(','))
                  ];
                  
                  const csv = csvRows.join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `transactions-${new Date().toISOString().split('T')[0]}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                className="p-5 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl hover:border-green-300 hover:shadow-md transition-all duration-200 group"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-white rounded-lg shadow-sm border">
                    <StatsIcons.download />
                  </div>
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900 group-hover:text-green-700">
                      {t('wallet.csvExport', 'CSV Export')}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {t('wallet.exportCSVDesc', 'Export filtered data for spreadsheets')}
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </motion.div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              {t('wallet.recentActivity', 'Recent Activity')}
            </h3>
            
            <div className="space-y-3">
              {recentTransactions.slice(0, 5).map((tx, index) => (
                <div
                  key={tx.id}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors duration-150"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${
                      tx.type === 'collect' 
                        ? 'bg-green-100 text-green-600' 
                        : tx.type === 'withdraw' 
                        ? 'bg-red-100 text-red-600' 
                        : 'bg-blue-100 text-blue-600'
                    }`}>
                      {tx.type === 'collect' ? (
                        <StatsIcons.revenue />
                      ) : tx.type === 'withdraw' ? (
                        <StatsIcons.withdraw />
                      ) : (
                        <StatsIcons.transactions />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {getTransactionTypeText(tx.type)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(tx.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-bold ${
                      tx.type === 'collect' || tx.type === 'refund' 
                        ? 'text-green-600' 
                        : 'text-red-600'
                    }`}>
                      {tx.type === 'collect' || tx.type === 'refund' ? '+' : '-'}${parseFloat(tx.amount).toFixed(2)}
                    </p>
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(tx.status)}`}>
                      {tx.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};